/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  batch,
  getCause,
  isAbortError,
  reatomAsync,
  withAssign,
  withConcurrency,
} from '@reatom/framework'

import { AsyncStateN, identity } from '@blade/std-lib'
import { Result } from '@blade/std-lib'

import type {
  Action,
  AsyncAction,
  AsyncCtx,
  Atom,
  AtomMut,
  Ctx,
  CtxParams,
  Fn,
} from '@reatom/framework'

// TODO: Restrict to single argument

interface Args<
  in out Params extends Array<any>,
  in out E,
  in out A,
  in out PrepareParams extends Array<any> = Params,
> {
  execAction: Action<Params, Promise<Result.Result<A, E>>>
  name?: string
  concurrently?: boolean
  initial?: AsyncStateN.AsyncStateN<E, A>
  onResult?: Fn<
    [Ctx, result: Result.Result<A, E>, params: PrepareParams],
    unknown
  >
}

export interface RefreshActionOptions {
  /**
   * 'full' — like execute with last params
   * 'success-only' — runs execution in background and refresh atom only in success case
   */
  strategy?: 'full' | 'success-only'
}

export interface RequestAtom<
  in Params extends ReadonlyArray<any>,
  in out E,
  in out A,
> extends AtomMut<AsyncStateN.AsyncStateN<E, A>> {
  execute: AsyncAction<
    CtxParams<[ctx: AsyncCtx, ...params: Params], never>,
    Result.Result<A, E>
  >
  reset: Action<[], void>
  refresh: Action<
    [options?: RefreshActionOptions],
    Promise<Result.Result<A, E> | undefined>
  >
  setState: Action<[AsyncStateN.AsyncStateN<E, A>], void>

  /** @deprecated */
  executeAction: AsyncAction<
    CtxParams<[ctx: AsyncCtx, ...params: Params], never>,
    Result.Result<A, E>
  >
  /** @deprecated */
  resetStateAction: Action<[], void>
  /** @deprecated */
  requestStateAtom: AtomMut<AsyncStateN.AsyncStateN<E, A>>
}

export declare namespace RequestAtom {
  export type AsAtomMut<T extends RequestAtom<any, any, any>> =
    T extends RequestAtom<any, infer E, infer A>
      ? AtomMut<AsyncStateN.AsyncStateN<E, A>>
      : never

  export type AsAtom<T extends RequestAtom<any, any, any>> =
    T extends RequestAtom<any, infer E, infer A>
      ? Atom<AsyncStateN.AsyncStateN<E, A>>
      : never
}

export function reatomRequest<Params extends Array<any>, E, A, Output>(
  args: Args<Params, E, A> & {
    mapOutput: (ctx: Ctx, result: A, params: Params) => Output
    prepare?: never
  },
): RequestAtom<Params, E, Output>

export function reatomRequest<
  Params extends Array<any>,
  E,
  A,
  PrepareParams extends Array<unknown>,
>(
  args: Args<Params, E, A, NoInfer<PrepareParams>> & {
    prepare: (ctx: Ctx, ...args: PrepareParams) => Params[0]
    mapOutput?: never
  },
): RequestAtom<PrepareParams, E, A>

export function reatomRequest<Params extends Array<any>, E, A>(
  args: Args<Params, E, A> & { prepare?: never; mapOutput?: never },
): RequestAtom<Params, E, A>

export function reatomRequest<
  Params extends Array<any>,
  E,
  A,
  PrepareParams extends Array<unknown>,
>(
  args: Args<Params, E, A> & {
    prepare?: never | ((ctx: Ctx, ...args: PrepareParams) => Params[0])
    mapOutput?: never | ((ctx: Ctx, result: A, params: Params) => A)
  },
): RequestAtom<Params & PrepareParams, E, A> {
  const {
    execAction,
    prepare,
    concurrently = true,
    onResult,
    initial,
    name,
    mapOutput,
  } = args
  const requestStateAtom = atom(
    initial ?? AsyncStateN.notInitiated<E, A>(),
    `${name}.requestStateAtom`,
  )

  const lastParamsAtom = atom<PrepareParams | Params | undefined>(
    undefined,
    `${name}.lastParams`,
  )

  const executeInternal = action(
    async (ctx, ...params: PrepareParams | Params) => {
      const preparedArgs = (
        prepare ? [prepare(ctx, ...(params as PrepareParams))] : params
      ) as Params

      requestStateAtom(ctx, AsyncStateN.loading())
      lastParamsAtom(ctx, params)

      let result
      try {
        result = await ctx.schedule(() => execAction(ctx, ...preparedArgs))
      } catch (error) {
        if (isAbortError(error)) {
          resetStateAction(ctx)
        }
        throw error
      }

      if (mapOutput && Result.isSuccess(result)) {
        const mapped = mapOutput(ctx, result.success, params as Params)
        requestStateAtom(ctx, AsyncStateN.success(mapped))

        return Result.succeed(mapped)
      }

      requestStateAtom(ctx, AsyncStateN.fromResult(result))
      return result
    },
    `${name}.executeAction.internal`,
  )

  const refreshSuccessOnlyInternal = action(
    async (ctx, ...params: PrepareParams | Params) => {
      const preparedArgs = (
        prepare ? [prepare(ctx, ...(params as PrepareParams))] : params
      ) as Params

      const result = await ctx.schedule((ctx) =>
        execAction(ctx, ...preparedArgs),
      )

      return batch(ctx, () => {
        if (Result.isFailure(result)) {
          return result
        }

        if (mapOutput) {
          const mapped = mapOutput(ctx, result.success, params as Params)
          requestStateAtom(ctx, AsyncStateN.success(mapped))
          const mappedResult = Result.succeed(mapped)
          onResult?.(ctx, mappedResult, params as Params)
          return mappedResult
        }

        requestStateAtom(ctx, AsyncStateN.success(result.success))
        onResult?.(ctx, result, params as Params)
        return result
      })
    },
    `${name}.executeAction.internal`,
  )

  const executeAction = reatomAsync(executeInternal, {
    onFulfill: (ctx, result) =>
      onResult?.(ctx, result, ctx.get(lastParamsAtom) as Params),
    name: `${name}.executeAction`,
  }).pipe(concurrently ? withConcurrency() : identity)

  const resetStateAction = action((ctx) => {
    requestStateAtom(ctx, initial ?? AsyncStateN.notInitiated())
  }, `${name}.resetStateAction`)

  const setStateAction = action((ctx, state) => {
    requestStateAtom(ctx, state)
  }, `${name}.setStateAction`)

  const refreshAction = action(
    async (
      ctx,
      options?: {
        strategy?: 'full' | 'success-only'
      },
    ): Promise<Result.Result<A, E> | undefined> => {
      const lastParams = ctx.get(lastParamsAtom)
      if (!lastParams) {
        return undefined
      }
      const strategy = options?.strategy ?? 'full'

      if (strategy === 'full') {
        return executeInternal(ctx, ...lastParams)
      }
      return refreshSuccessOnlyInternal(ctx, ...lastParams)
    },
    `${name}.resetStateAction`,
  )

  return requestStateAtom.pipe(
    withAssign(() => ({
      requestStateAtom,
      execute: executeAction,
      reset: resetStateAction,
      refresh: refreshAction,
      setState: setStateAction,
      executeAction,
      resetStateAction,
      setStateAction,
    })),
  )
}

export const logRequestAction = <P extends Array<unknown>, E, A>(
  act: Action<P, Promise<Result.Result<A, E>>>,
) => {
  act.onCall((ctx, _, req) => {
    const uns = ctx.subscribe((logs) => {
      for (const it of logs) {
        if (it.proto === act.__reatom) {
          // eslint-disable-next-line no-console
          console.log(
            `[DEMO] [Request Start] %s \nrequest: %O \ncause %O`,
            act.__reatom.name,
            req,
            getCause(it),
          )
        }
      }
      uns()
    })
  })
}
